'use client';

import { Sparkles, Send, Loader2, RefreshCw, FileText, Users, ChevronDown } from '@/components/ui/icons';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useConversationHistory, useSendMessage, type Message, type ChatMode, type SourceCitation, type ArtefactCitation } from '@/hooks/use-conversations';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Chat mode options
const CHAT_MODES: { value: ChatMode; label: string; description: string }[] = [
  { value: 'kb', label: 'KB Only', description: 'Search knowledge base documents only' },
  { value: 'kb_templates', label: 'KB + Artefacts', description: 'Include project artefacts (personas, use cases, etc.)' },
  { value: 'kb_templates_external', label: 'KB + Artefacts + Web', description: 'Also search external web sources' },
];

interface QuickAction {
  id: string;
  label: string;
  prompt: string;
}

const quickActions: QuickAction[] = [
  { id: 'personas', label: 'Generate user personas', prompt: 'Generate user personas based on our project requirements and target audience.' },
  { id: 'competitors', label: 'Analyze competitors', prompt: 'Analyze our main competitors and provide insights on market positioning.' },
  { id: 'service-map', label: 'Create service map', prompt: 'Create a service blueprint mapping our key user journeys and touchpoints.' },
  { id: 'requirements', label: 'Draft requirements', prompt: 'Draft functional and non-functional requirements based on our project goals.' },
];

interface ChatInterfaceProps {
  projectId: string;
  projectName?: string;
  conversationId: string | null;
  onStartConversation?: (message: string) => Promise<string | null>;
}

interface LocalMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isLoading?: boolean;
  messageText?: string; // Store the original message text for matching
  sourcesUsed?: SourceCitation[];
  artefactsUsed?: ArtefactCitation[];
}

export function ChatInterface({
  projectId,
  projectName = 'Project',
  conversationId,
  onStartConversation,
}: ChatInterfaceProps) {
  const [message, setMessage] = useState('');
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>('kb');
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const modeDropdownRef = useRef<HTMLDivElement>(null);

  const sendMessageMutation = useSendMessage(projectId, conversationId, chatMode);

  // Close mode dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modeDropdownRef.current && !modeDropdownRef.current.contains(event.target as Node)) {
        setShowModeDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const { data: conversationData, isLoading: isLoadingConversation } = useConversationHistory(
    projectId,
    conversationId
  );
  
  // Track the last user message we sent to match loading placeholders with responses
  const lastSentMessageRef = useRef<string | null>(null);
  
  // Track message count to detect when new assistant responses arrive
  const previousMessageCountRef = useRef(0);
  
  // Track previous conversation ID to detect when we're creating a new conversation vs switching
  const previousConversationIdRef = useRef<string | null>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages, conversationData?.messages]);

  // Reset local messages when conversation changes
  // BUT don't reset if we're creating a new conversation (conversationId goes from null to a value)
  // because we want to keep the loading placeholder that was just added
  useEffect(() => {
    const prevId = previousConversationIdRef.current;
    previousConversationIdRef.current = conversationId;
    
    // Only reset if we're switching to a different existing conversation
    // Not if we're creating a new one (null -> id) or if conversationId is null
    if (conversationId && prevId && prevId !== conversationId) {
      // Switching between existing conversations - clear local messages
      setLocalMessages([]);
      previousMessageCountRef.current = 0;
    } else if (conversationId === null && prevId) {
      // Going back to no conversation - clear local messages
      setLocalMessages([]);
      previousMessageCountRef.current = 0;
    }
    // If prevId is null and conversationId is set, we're creating a new conversation
    // Don't clear local messages - keep the loading placeholder
  }, [conversationId]);
  
  // Initialize message count when conversation data loads
  useEffect(() => {
    if (conversationData?.messages) {
      previousMessageCountRef.current = conversationData.messages.length;
    }
  }, [conversationId]); // Only when conversation changes
  
  // Clear local messages that have been saved to the database
  useEffect(() => {
    if (conversationData?.messages) {
      const currentMessageCount = conversationData.messages.length;
      const previousCount = previousMessageCountRef.current;
      
      // Check if we have new messages (message count increased)
      const hasNewMessages = currentMessageCount > previousCount;
      
      // Check if the last message is an assistant message (new AI response)
      const lastMessage = conversationData.messages[currentMessageCount - 1];
      const hasNewAssistantResponse = hasNewMessages && lastMessage?.role === 'assistant';
      
      // Update the ref for next time
      previousMessageCountRef.current = currentMessageCount;
      
      // Build a map of user messages to their responses
      // Only mark as having a response if there's an actual assistant message with content after it
      const userMessageToResponse = new Map<string, boolean>();
      conversationData.messages.forEach((msg, index) => {
        if (msg.role === 'user') {
          // Find the next assistant message after this user message
          const messagesAfter = conversationData.messages.slice(index + 1);
          const nextAssistant = messagesAfter.find(m => m.role === 'assistant');
          // Only mark as having response if there's an assistant message AND it has non-empty content
          // This is important for the first message - don't remove loading until we have actual content
          const hasResponse = !!nextAssistant && nextAssistant.content && nextAssistant.content.trim().length > 0;
          userMessageToResponse.set(msg.content.trim(), hasResponse);
        }
      });
      
      if (localMessages.length > 0) {
        setLocalMessages(prev => {
          const conversationMessageIds = new Set(conversationData.messages.map(m => m.id));
          const conversationUserMessages = new Set(
            conversationData.messages
              .filter(m => m.role === 'user')
              .map(m => m.content.trim())
          );
          
          return prev.filter(localMsg => {
            // Keep loading placeholders that don't have a corresponding assistant response yet
            if (localMsg.isLoading) {
              // Check if the corresponding user message has a response
              const userMessage = localMsg.messageText?.trim() || '';
              const hasResponse = userMessageToResponse.get(userMessage) || false;
              
              if (hasResponse) {
                // This loading placeholder has been fulfilled, remove it
                return false;
              }
              // Keep this loading placeholder - it's still waiting
              return true;
            }
            
            // For user messages, only remove if they exist in conversation data
            if (localMsg.role === 'user') {
              // Remove if the exact message exists in conversation data
              return !conversationUserMessages.has(localMsg.content.trim());
            }
            
            // For other messages, remove if they exist in conversation data (by ID)
            return !conversationMessageIds.has(localMsg.id);
          });
        });
      }
    }
  }, [conversationData?.messages, localMessages.length]);

  const handleSendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    // Track this message so we can match it with the response
    lastSentMessageRef.current = messageText;
    
    const now = new Date().toISOString();
    const userMessageId = `local-user-${Date.now()}`;
    const loadingId = `loading-${Date.now()}-${messageText.slice(0, 10)}`;
    
    // Add user message optimistically (appears immediately)
    // Then add loading placeholder for AI response
    setLocalMessages(prev => [...prev, 
      {
        id: userMessageId,
        role: 'user',
        content: messageText,
        timestamp: now,
        messageText: messageText,
      },
      {
        id: loadingId,
        role: 'assistant',
        content: '',
        timestamp: now,
        isLoading: true,
        messageText: messageText, // Store the message text to match with response
      }
    ]);
    setMessage('');
    setIsLoading(true);

    try {
      let currentConversationId = conversationId;

      // If no conversation exists, start a new one
      if (!currentConversationId && onStartConversation) {
        currentConversationId = await onStartConversation(messageText);
        // After creating conversation, the initial message is already saved
        // We need to trigger the AI response for that existing message
        if (currentConversationId) {
          // Wait a moment for the conversation hook to update
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Send the message to trigger AI response
          // This will create a duplicate user message, but we'll filter it out
          const response = await fetch(
            `/api/projects/${projectId}/conversations/${currentConversationId}/messages`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
              },
              body: JSON.stringify({ content: messageText, mode: chatMode }),
            }
          );

          if (!response.ok) {
            throw new Error('Failed to send message');
          }
          // Don't clear loading placeholder - let it stay until conversationData refetches
        }
        return;
      }

      // For existing conversations, use the mutation
      if (currentConversationId) {
        await sendMessageMutation.mutateAsync(messageText);
        
        // Don't clear loading placeholder here - let it stay until conversationData refetches
        // The useEffect below will clear it when the actual assistant message appears
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Replace loading with error message
      setLocalMessages(prev => {
        const filtered = prev.filter(m => !m.isLoading);
        return [...filtered, {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date().toISOString(),
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(message);
  };

  const handleQuickAction = (action: QuickAction) => {
    handleSendMessage(action.prompt);
  };

  const handleResubmitMessage = async (messageContent: string) => {
    await handleSendMessage(messageContent);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  // Combine conversation history with local messages, ensuring no duplicates
  // Use message ID as primary key (database messages have unique IDs)
  // Only deduplicate by content for user messages to prevent accidental duplicates
  const allMessages = useMemo(() => {
    const conversationMessages = conversationData?.messages || [];
    
    // Use message ID as the key for database messages (they're unique)
    // For local messages, use a combination to ensure uniqueness
    const messageMap = new Map<string, Message | LocalMessage>();
    const seenUserMessageSignatures = new Set<string>();
    
    // First, add all conversation messages (from database) - these take priority
    // Use ID as key since database messages have unique IDs
    // Track consecutive duplicate user messages (common with first message)
    let lastUserMessage: Message | null = null;
    conversationMessages.forEach((msg, index) => {
      // Check if this is a duplicate user message (same content as previous user message)
      if (msg.role === 'user' && lastUserMessage && msg.content.trim() === lastUserMessage.content.trim()) {
        return;
      }
      
      messageMap.set(msg.id, msg);
      
      // Track user messages by signature to catch duplicates
      if (msg.role === 'user') {
        const signature = `${msg.role}:${msg.content.trim()}`;
        seenUserMessageSignatures.add(signature);
        lastUserMessage = msg;
      } else {
        lastUserMessage = null; // Reset when we see an assistant message
      }
    });
    
    // Track which user messages from conversation data have corresponding assistant responses
    // Only mark as having response if the assistant message has actual content
    const userMessagesWithResponses = new Set<string>();
    conversationMessages.forEach((msg, index) => {
      if (msg.role === 'user') {
        // Check if there's an assistant message with content after this user message
        const nextAssistant = conversationMessages.slice(index + 1).find(m => m.role === 'assistant');
        const hasResponse = !!nextAssistant && nextAssistant.content && nextAssistant.content.trim().length > 0;
        if (hasResponse) {
          userMessagesWithResponses.add(msg.content.trim());
        }
      }
    });
    
    // Then add local messages that don't already exist
    localMessages.forEach(localMsg => {
      // Always show loading placeholders (use unique ID as key)
      if (localMsg.isLoading) {
        // Only show loading if the corresponding user message doesn't have a response yet
        // AND the actual assistant message is not yet in conversation data
        const userMessageText = localMsg.messageText?.trim() || '';
        const userMessageHasResponse = userMessagesWithResponses.has(userMessageText);
        
        // Check if there's an actual assistant message with content in conversation data for this user message
        const hasActualAssistantMessage = conversationMessages.some((msg, index) => {
          if (msg.role === 'user' && msg.content.trim() === userMessageText) {
            // Check if there's an assistant message with content after this user message
            const nextAssistant = conversationMessages.slice(index + 1).find(m => m.role === 'assistant');
            return !!nextAssistant && nextAssistant.content && nextAssistant.content.trim().length > 0;
          }
          return false;
        });
        
        // Only show loading if we don't have the actual assistant message with content yet
        if (!hasActualAssistantMessage) {
          messageMap.set(`loading-${localMsg.id}`, localMsg as Message);
        }
        return;
      }
      
      // For user messages, check if same content already exists in database
      if (localMsg.role === 'user') {
        const signature = `${localMsg.role}:${localMsg.content.trim()}`;
        // Only add if not already in database AND doesn't have a response yet
        const hasResponse = userMessagesWithResponses.has(localMsg.content.trim());
        if (!seenUserMessageSignatures.has(signature) && !hasResponse) {
          messageMap.set(localMsg.id, localMsg as Message);
          seenUserMessageSignatures.add(signature);
        }
      } else {
        // For assistant messages, always add them (they're unique per user message)
        messageMap.set(localMsg.id, localMsg as Message);
      }
    });
    
    // Convert map to array and sort by timestamp
    const sorted = Array.from(messageMap.values()).sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB;
    });
    
    return sorted;
  }, [conversationData?.messages, localMessages]);

  const hasMessages = allMessages.length > 0;

  return (
    <div className="flex-1 bg-gray-50 flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container px-6 py-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-primary" />
            <h2 className="text-base font-normal text-gray-900 tracking-tight">
              AI Assistant
            </h2>
          </div>
          {/* Mode Toggle */}
          <div className="relative" ref={modeDropdownRef}>
            <button
              onClick={() => setShowModeDropdown(!showModeDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              aria-haspopup="listbox"
              aria-expanded={showModeDropdown}
              aria-label={`Chat mode: ${CHAT_MODES.find(m => m.value === chatMode)?.label}`}
            >
              <span className="text-gray-700">
                {CHAT_MODES.find(m => m.value === chatMode)?.label}
              </span>
              <ChevronDown className={cn("w-4 h-4 text-gray-500 transition-transform", showModeDropdown && "rotate-180")} />
            </button>
            {showModeDropdown && (
              <div
                className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                role="listbox"
                aria-label="Select chat mode"
              >
                {CHAT_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    role="option"
                    aria-selected={chatMode === mode.value}
                    onClick={() => {
                      setChatMode(mode.value);
                      setShowModeDropdown(false);
                    }}
                    className={cn(
                      "w-full text-left px-4 py-3 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg",
                      chatMode === mode.value && "bg-brand-primary/5"
                    )}
                  >
                    <div className="text-sm font-medium text-gray-900">{mode.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{mode.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <p className="text-base text-gray-600 tracking-tight">
          Ask questions about your project or generate artifacts
        </p>
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="container px-6 py-4">
        {isLoadingConversation ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
          </div>
        ) : !hasMessages ? (
          /* Welcome message for new conversation */
          <div>
            <p className="text-base text-gray-800 leading-7">
              Hello! I'm your AI design assistant for <strong>{projectName}</strong>. I've analyzed all your project documents and can help you with:
            </p>
            <ul className="mt-3 space-y-2 text-base text-gray-800 leading-7">
              <li>• Generating discovery packs and research summaries</li>
              <li>• Creating user personas and journey maps</li>
              <li>• Analyzing competitors and market positioning</li>
              <li>• Drafting requirements and specifications</li>
              <li>• Generating mid-fi prototypes</li>
            </ul>
            <p className="mt-4 text-base text-gray-800 leading-7">
              What would you like to work on today?
            </p>
          </div>
        ) : (
          /* Message history */
          <div className="space-y-4">
            {allMessages.map((msg, index) => {
              // Check if this is a user message
              const isUserMessage = msg.role === 'user';
              
              // Find the loading placeholder for this user message
              const loadingPlaceholder = localMessages.find(
                m => m.isLoading && m.messageText?.trim() === msg.content.trim()
              );
              
              // Check if there's an assistant response for this user message in conversation data
              // Must have actual content, not just an empty message
              const userMessageIndex = conversationData?.messages?.findIndex(
                m => m.role === 'user' && m.content.trim() === msg.content.trim()
              ) ?? -1;
              
              let hasAssistantResponse = false;
              if (userMessageIndex >= 0) {
                const nextAssistant = conversationData?.messages?.slice(userMessageIndex + 1).find(m => m.role === 'assistant');
                // Only consider it a response if it has actual content
                hasAssistantResponse = !!nextAssistant && nextAssistant.content.trim().length > 0;
              }
              
              // Show loading only if we have a placeholder AND no assistant response yet
              const showLoading = loadingPlaceholder && !hasAssistantResponse;
              
              return (
                <div key={msg.id || index}>
                  {/* User Message */}
                  {isUserMessage && (
                    <div className="flex flex-col items-end mb-2">
                      <div className="max-w-[80%] rounded-[10px] p-4 relative bg-brand-primary text-white">
                        <p className="text-base leading-6 whitespace-pre-wrap pr-8">
                          {msg.content}
                        </p>
                        <button
                          onClick={() => handleResubmitMessage(msg.content)}
                          disabled={isLoading}
                          className="absolute top-2 right-2 w-7 h-7 bg-white/25 hover:bg-white/35 rounded-full flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Resubmit message"
                          title="Resubmit this message"
                        >
                          <RefreshCw className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      <span className="text-xs mt-1 px-2 text-gray-500">
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    </div>
                  )}
                  
                  {/* Loading Indicator - appears right after user message, only if no response yet */}
                  {isUserMessage && showLoading && (
                    <div className="flex flex-col items-start mb-2">
                      <div className="max-w-[80%] rounded-[10px] p-4 bg-white shadow-sm">
                        <div className="flex items-center gap-3 py-2">
                          <div className="relative flex-shrink-0">
                            <Loader2 className="w-5 h-5 text-brand-primary animate-spin" />
                            <div className="absolute inset-0 w-5 h-5 border-2 border-brand-primary/20 rounded-full animate-ping" />
                          </div>
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-gray-700">
                              AI is thinking...
                            </span>
                            <span className="text-xs text-gray-500">
                              Generating response
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Assistant Message - only show if not loading and we have content */}
                  {msg.role === 'assistant' && !(msg as LocalMessage).isLoading && msg.content && (
                    <div className="mb-4">
                      <div className="text-base leading-7 text-gray-800 prose prose-base max-w-none prose-p:my-3 prose-ul:my-3 prose-ol:my-3 prose-li:my-1 prose-headings:my-4 prose-strong:text-gray-900 prose-ul:list-disc prose-ol:list-decimal">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>

                      {/* Sources Used */}
                      {msg.sourcesUsed && msg.sourcesUsed.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-1.5 mb-2">
                            <FileText className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs font-medium text-gray-500">Sources Used</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {msg.sourcesUsed.slice(0, 5).map((source, idx) => (
                              <span
                                key={`${source.documentId}-${idx}`}
                                className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 cursor-default"
                                title={source.excerpt}
                              >
                                {source.documentName}
                              </span>
                            ))}
                            {msg.sourcesUsed.length > 5 && (
                              <span className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-md">
                                +{msg.sourcesUsed.length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Artefacts Used */}
                      {msg.artefactsUsed && msg.artefactsUsed.length > 0 && (
                        <div className={cn("mt-3", !msg.sourcesUsed?.length && "mt-4 pt-3 border-t border-gray-100")}>
                          <div className="flex items-center gap-1.5 mb-2">
                            <Users className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs font-medium text-gray-500">Artefacts Used</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {msg.artefactsUsed.map((artefact, idx) => (
                              <a
                                key={`${artefact.id}-${idx}`}
                                href={artefact.deepLink || '#'}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-brand-primary/10 text-brand-primary rounded-md hover:bg-brand-primary/20 transition-colors"
                                title={`${artefact.type}: ${artefact.name}`}
                              >
                                <span className="font-medium">{artefact.name}</span>
                                <span className="text-brand-primary/60">({artefact.type})</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      <span className="text-xs mt-2 block text-gray-400">
                        {formatTimestamp(msg.timestamp)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
        </div>
      </div>

      {/* Bottom section - Quick Actions + Input fixed to bottom */}
      <div className="shrink-0 mt-auto bg-white border-t border-gray-200">
        <div className="container">
        {/* Quick Actions - only show for new conversations */}
        {!hasMessages && (
          <div className="px-6 py-3 border-b border-gray-100">
            <p className="text-base text-gray-600 mb-3">Quick actions:</p>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  disabled={isLoading}
                  className="h-9 px-3 bg-white border border-gray-300 rounded-[10px] text-base text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message Input */}
        <div className="px-6 py-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ask a question or request an artifact..."
              disabled={isLoading}
              className="flex-1 h-[50px] px-4 bg-gray-100 rounded-[10px] text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isLoading || !message.trim()}
              className="w-[68px] h-[50px] bg-brand-primary rounded-[10px] flex items-center justify-center hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Send className="w-5 h-5 text-white" />
              )}
            </button>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
}

